import { useState, useEffect } from 'react';
import {
  Stack,
  Group,
  Paper,
  Text,
  Avatar,
  Badge,
  ActionIcon,
  Tooltip,
  Table,
  Menu,
  Modal,
  Button,
  Select,
  LoadingOverlay,
  TextInput,
  Box,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatCurrency } from '../../utils/formatters';
import { useGroupMembers as useGroupMembersActions, type Member } from '../../hooks/useGroupMembers';
import { useAuthInfo } from '@propelauth/react';
import { env } from '../../utils/env';
import settingsIcon from '../../assets/icons/settings.svg';
import emailIcon from '../../assets/icons/email.svg';
import adminIcon from '../../assets/icons/admin.svg';
import userIcon from '../../assets/icons/user.svg';
import plusIcon from '../../assets/icons/plus.svg';
import alertCircleIcon from '../../assets/icons/alert-circle.svg';
import { InviteMemberModal } from './InviteMemberModal';

interface Balance {
  from_member_id: string;
  to_member_id: string;
  amount: number;
  currency_code: string;
}

interface MemberListProps {
  groupId: string;
  members: Member[];
  balances: Balance[];
  accessToken: string;
  onMemberChange: () => void;
}

export const MemberList = ({ groupId, members: initialMembers, balances, accessToken, onMemberChange }: MemberListProps) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [showAddGhostModal, setShowAddGhostModal] = useState(false);
  const [ghostName, setGhostName] = useState('');
  const [ghostEmail, setGhostEmail] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [members, setMembers] = useState<Member[]>(initialMembers);
  const { user } = useAuthInfo();

  const { loading: actionLoading, removeMember, updateMemberRole, remindToJoin, addGhostMember } = useGroupMembersActions(groupId, accessToken);

  useEffect(() => {
    setMembers(initialMembers);
  }, [initialMembers]);

  const currentMember = members.find(m => m.user_id === user?.userId);
  const currentUserRole = currentMember?.role || 'member';
  const canManageMembers = ['owner', 'admin'].includes(currentUserRole);

  const ghostMemberCount = members.filter(m => m.status === 'ghost').length;
  const tempGhostCount = members.filter(m => m.status === 'ghost' && m.notification_preferences?.is_temporary_ghost).length;
  const regularGhostCount = ghostMemberCount - tempGhostCount;

  const getGhostLimit = () => {
    if (ghostMemberCount >= 20) return 20;
    if (ghostMemberCount >= 7) return 7;
    return 3;
  };

  const isGhostMember = (member: Member): boolean => {
    return member.status === 'ghost' || member.is_ghost === true;
  };

  const handleRemoveMemberWrapper = async (memberId: string, member: Member) => {
    try {
      const isGhost = isGhostMember(member);
      await removeMember(memberId, isGhost);
      onMemberChange();
      notifications.show({
        title: 'Success',
        message: 'Member removed successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to remove member',
        color: 'red',
      });
    }
  };

  const handleUpdateRoleWrapper = async () => {
    if (!selectedMember || !newRole) return;
    try {
      await updateMemberRole(selectedMember.id, newRole as 'admin' | 'member');
      onMemberChange();
      notifications.show({
        title: 'Success',
        message: `Member role updated to ${newRole}`,
        color: 'green',
      });
      setShowSettingsModal(false);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update member role',
        color: 'red',
      });
    }
  };

  const handleRemindToJoin = async (email: string) => {
    try {
      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members/remind`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to send reminder');
      }

      notifications.show({
        title: 'Success',
        message: 'Invitation reminder sent',
        color: 'green',
      });
    } catch (error) {
      console.error('Error sending reminder:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to send reminder',
        color: 'red',
      });
    }
  };

  const getMemberStatus = (status: string, member: Member) => {
    if (isGhostMember(member)) {
      return {
        label: 'Ghost',
        color: 'grape',
      };
    }

    switch (status) {
      case 'active':
        return {
          label: 'Active',
          color: 'green',
        };
      case 'pending':
        return {
          label: 'Pending',
          color: 'yellow',
        };
      default:
        return {
          label: status.charAt(0).toUpperCase() + status.slice(1),
          color: 'gray',
        };
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <img src={adminIcon} alt="Owner" width={14} />;
      case 'admin':
        return <img src={adminIcon} alt="Admin" width={14} />;
      default:
        return <img src={userIcon} alt="Member" width={14} />;
    }
  };

  const memberBalances = members.map(member => {
    let totalOwed = 0;
    let totalOwes = 0;
    let currency_code = 'USD';

    const memberBalanceForCurrency = balances.find(balance =>
      balance.from_member_id === member.id || balance.to_member_id === member.id
    );

    if (memberBalanceForCurrency) {
      currency_code = memberBalanceForCurrency.currency_code;
    }

    balances.forEach(balance => {
      if (balance.from_member_id === member.id) {
        totalOwes += balance.amount;
      }
      if (balance.to_member_id === member.id) {
        totalOwed += balance.amount;
      }
    });

    return {
      ...member,
      totalOwed,
      totalOwes,
      netBalance: totalOwed - totalOwes,
      currency_code
    };
  });

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      signDisplay: 'auto',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleAddGhostMemberWrapper = async () => {
    if (!ghostName.trim()) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please enter a display name for the ghost member',
        color: 'red',
      });
      return;
    }

    try {
      await addGhostMember(ghostName.trim(), ghostEmail.trim() || undefined);
      setShowAddGhostModal(false);
      setGhostName('');
      setGhostEmail('');
      onMemberChange();
      notifications.show({
        title: 'Success',
        message: 'Ghost member added successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to add ghost member',
        color: 'red',
      });
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={500} size="lg">Members ({members.length})</Text>
          {ghostMemberCount > 0 && (
            <Tooltip
              label="Ghost members are placeholders for people who don't use the app. They can have expenses split with them but won't receive notifications."
              position="right"
              multiline
              w={220}
            >
              <Badge color="gray" variant="dot" size="sm">
                {ghostMemberCount} Ghost{ghostMemberCount !== 1 ? 's' : ''}
                <img src={alertCircleIcon} alt="Info" width={12} style={{ marginLeft: 4 }} />
              </Badge>
            </Tooltip>
          )}

          {tempGhostCount > 0 && (
            <Tooltip
              label="These are temporary placeholders for invited members who haven't accepted yet. They'll be replaced when the member accepts the invitation."
              position="right"
              multiline
              w={240}
            >
              <Badge color="blue" variant="dot" size="sm">
                {tempGhostCount} Waiting
                <img src={alertCircleIcon} alt="Info" width={12} style={{ marginLeft: 4 }} />
              </Badge>
            </Tooltip>
          )}
        </Group>
        {canManageMembers && (
          <Button
            leftSection={<img src={plusIcon} alt="Add Member" width={14} />}
            onClick={() => setShowInviteModal(true)}
          >
            Add Member
          </Button>
        )}
      </Group>

      <Paper withBorder style={{ position: 'relative' }}>
        <LoadingOverlay visible={actionLoading} />
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Member</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>They Owe</Table.Th>
              <Table.Th>They're Owed</Table.Th>
              <Table.Th>Net Balance</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {memberBalances.map(member => {
              const status = getMemberStatus(member.status, member);

              return (
                <Table.Tr key={member.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar
                        src={member.avatar_url}
                        radius="xl"
                        size="sm"
                      >
                        {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                      </Avatar>
                      <Stack gap={0}>
                        <Text size="sm">
                          {member.display_name || member.email}
                          {member.notification_preferences?.is_temporary_ghost && (
                            <Tooltip label="This member was invited but hasn't accepted yet">
                              <span style={{ marginLeft: 5, opacity: 0.7 }}>(invited)</span>
                            </Tooltip>
                          )}
                        </Text>
                        <Text size="xs" c="dimmed">{member.email}</Text>
                      </Stack>
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    <Badge
                      leftSection={getRoleIcon(member.role)}
                      color={member.role === 'owner' ? 'blue' : member.role === 'admin' ? 'indigo' : 'gray'}
                    >
                      {member.role}
                    </Badge>
                  </Table.Td>

                  <Table.Td>
                    <Badge color={status.color}>
                      {status.label}
                    </Badge>
                  </Table.Td>

                  <Table.Td>
                    <Text
                      c={member.totalOwes > 0 ? 'green' : 'dimmed'}
                      fw={member.totalOwes > 0 ? 500 : 400}
                    >
                      {formatCurrency(member.totalOwes, member.currency_code)}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    <Text
                      c={member.totalOwed > 0 ? 'green' : 'dimmed'}
                      fw={member.totalOwed > 0 ? 500 : 400}
                    >
                      {formatCurrency(member.totalOwed, member.currency_code)}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    <Text
                      c={member.netBalance > 0 ? 'green' : member.netBalance < 0 ? 'red' : 'dimmed'}
                      fw={member.netBalance !== 0 ? 500 : 400}
                    >
                      {formatCurrency(member.netBalance, member.currency_code)}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    <Group gap="xs">
                      {member.status === 'pending' && (
                        <Tooltip label="Send reminder">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleRemindToJoin(member.email)}
                          >
                            <img src={emailIcon} alt="Remind" width={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}

                      {canManageMembers && member.role !== 'owner' && (
                        <Menu position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle">
                              <img src={settingsIcon} alt="Settings" width={16} />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            {!isGhostMember(member) && (
                              <Menu.Item
                                leftSection={<img src={adminIcon} alt="Change Role" width={14} />}
                                onClick={() => {
                                  setSelectedMember(member);
                                  setNewRole(member.role);
                                  setShowSettingsModal(true);
                                }}
                              >
                                Change Role
                              </Menu.Item>
                            )}

                            <Menu.Item
                              color="red"
                              onClick={() => handleRemoveMemberWrapper(member.id, member)}
                            >
                              Remove
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      <InviteMemberModal
        opened={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          onMemberChange();
        }}
        groupId={parseInt(groupId)}
        accessToken={accessToken}
        ghostCount={regularGhostCount}
        ghostLimit={getGhostLimit()}
      />

      <Modal
        opened={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title="Change Member Role"
        centered
      >
        {selectedMember && (
          <Stack>
            <Group>
              <Avatar
                src={selectedMember.avatar_url}
                radius="xl"
                size="md"
              >
                {selectedMember.display_name ? selectedMember.display_name.charAt(0) : selectedMember.email.charAt(0)}
              </Avatar>
              <Stack gap={0}>
                <Text>{selectedMember.display_name || selectedMember.email}</Text>
                <Text size="sm" c="dimmed">{selectedMember.email}</Text>
              </Stack>
            </Group>

            <Select
              label="Select new role"
              value={newRole}
              onChange={(value) => value && setNewRole(value)}
              data={[
                { value: 'admin', label: 'Admin' },
                { value: 'member', label: 'Member' },
              ]}
            />

            <Text size="sm" c="dimmed">
              {newRole === 'admin'
                ? 'Admins can manage expenses, members, and group settings'
                : 'Members can create expenses and participate in discussions'}
            </Text>

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRoleWrapper}>
                Update Role
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={showAddGhostModal}
        onClose={() => setShowAddGhostModal(false)}
        title="Add Ghost Member"
        centered
      >
        <Stack>
          <TextInput
            label="Display Name"
            value={ghostName}
            onChange={(e) => setGhostName(e.target.value)}
          />
          <TextInput
            label="Email"
            value={ghostEmail}
            onChange={(e) => setGhostEmail(e.target.value)}
          />
          <Text size="sm" c="dimmed">
            Ghost members are placeholders for people who don't use the app. They can have expenses split with them but won't receive notifications.
          </Text>
          <Group justify="center" mt="md">
            <Button
              variant="outline"
              onClick={() => setShowAddGhostModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAddGhostMemberWrapper}>
              Add Ghost Member
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}; 